'use client';

import { ModalWrapper, ModalHeader } from '@/components/modal';

interface OrderGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  videoUrl: string;
}

/**
 * YouTube 영상 URL에서 video ID를 추출합니다.
 * 지원 형식:
 * - https://youtu.be/VIDEO_ID
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID
 */
const YOUTUBE_VIDEO_ID_PATTERN = /^[a-zA-Z0-9_-]{11}$/;

function getYouTubeVideoId(url: string): string | null {
  const patterns = [
    /youtu\.be\/([^?&]+)/,
    /youtube\.com\/watch\?v=([^&]+)/,
    /youtube\.com\/embed\/([^?&]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      const videoId = match[1];
      if (YOUTUBE_VIDEO_ID_PATTERN.test(videoId)) {
        return videoId;
      }
    }
  }
  return null;
}

export function OrderGuideModal({
  isOpen,
  onClose,
  title,
  videoUrl,
}: OrderGuideModalProps) {
  const videoId = getYouTubeVideoId(videoUrl);

  if (!videoId) return null;

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose} position="center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-guide-modal-title"
        className="relative flex w-[calc(100vw-40px)] max-w-[710px] flex-col overflow-hidden rounded-[20px] bg-white shadow-[0px_2px_6px_0px_rgba(0,0,0,0.06),0px_32px_40px_-16px_rgba(0,0,0,0.12)]"
      >
        <ModalHeader
          title={title}
          onClose={onClose}
          titleId="order-guide-modal-title"
        />

        {/* Video Container */}
        <div className="px-5 pb-5 md:px-10 md:pb-10">
          <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-[#ECECEC]">
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
              title={title}
              allow="autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              sandbox="allow-scripts allow-same-origin allow-presentation"
              referrerPolicy="strict-origin-when-cross-origin"
              className="absolute inset-0 size-full"
            />
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
}

export default OrderGuideModal;
